import {
  ApplicationRef,
  ComponentRef,
  EnvironmentInjector,
  Injectable,
  NgZone,
  OutputRefSubscription,
  createComponent,
  inject,
} from '@angular/core';
import { TransitionService } from '@uirouter/core';
import { FtContestInvitationShareComponent } from './ft-contest-invitation-share.component';

/**
 * WS5 #10 Stage A inc 2v — native port of the AngularJS `invitationShareModal`
 * service (account-v1/.../photo-advocacy/contest/invitationShareModal.service.js):
 * the same `show(...)` signature, the same SINGLE-INSTANCE `inProgress` guard
 * (unlike the per-call BroadcastModal — a second share click while open is a
 * no-op until the first closes). Where v1 `$compile`d
 * `<ft-contest-invitation-share>` onto a fresh `$rootScope.$new(true)` and the
 * embedded ftModals body-appended themselves, this mounts the native host onto
 * `document.body` (the BroadcastModalService recipe); the host's `closed`
 * output (v1 'modal-invitationShare.cancel' → scope.$destroy) tears the
 * instance down and re-arms the guard.
 *
 * `propertyBackground` is derived here exactly like v1
 * (`property.BackgroundImageUrl`, service.js:28).
 *
 * Navigation teardown: v1 had a service-level `$locationChangeSuccess`
 * (hide → destroy → scope.$destroy) plus the ftModal directive's
 * `$locationChangeStart` hide — net, any navigation closed the whole flow.
 * Natively one UIRouter `TransitionService.onSuccess` hook per open instance
 * does the same (deregistered on teardown).
 */
@Injectable({ providedIn: 'root' })
export class InvitationShareModalService {
  /** v1 InvitationShareModal.prototype.inProgress. */
  private inProgress = false;

  private readonly appRef = inject(ApplicationRef);
  private readonly environmentInjector = inject(EnvironmentInjector);
  private readonly zone = inject(NgZone);
  private readonly transitions = inject(TransitionService);

  show(
    contestUUID: any,
    companyCode: any,
    contestName: any,
    invitationBackground: any,
    invitationUrl: any,
    property: any,
    network: any,
    languageCode: any,
  ): void {
    if (this.inProgress) return;
    this.inProgress = true;

    this.zone.run(() => {
      const ref: ComponentRef<FtContestInvitationShareComponent> = createComponent(FtContestInvitationShareComponent, {
        environmentInjector: this.environmentInjector,
      });

      // The v1 scope extension (service.js:21-31), name-for-name.
      ref.setInput('contestUuid', contestUUID);
      ref.setInput('companyCode', companyCode);
      ref.setInput('property', property);
      ref.setInput('contest', contestName);
      ref.setInput('network', network);
      ref.setInput('invitationBackground', invitationBackground);
      ref.setInput('propertyBackground', property.BackgroundImageUrl);
      ref.setInput('invitationUrl', invitationUrl);
      ref.setInput('languageCode', languageCode);

      let closeSub: OutputRefSubscription | undefined;
      let unhookTransition: Function | undefined;
      let torndown = false;
      const teardown = () => {
        if (torndown) return;
        torndown = true;
        closeSub?.unsubscribe();
        unhookTransition?.();
        this.appRef.detachView(ref.hostView);
        ref.destroy();
        ref.location.nativeElement.remove();
        // v1 scope.$on('$destroy') → inProgress = false (service.js:41-43).
        this.inProgress = false;
      };
      closeSub = ref.instance.closed.subscribe(() => teardown());
      // v1 $locationChangeSuccess → hide + destroy (service.js:44-50).
      unhookTransition = this.transitions.onSuccess({}, () => teardown()) as unknown as Function;

      this.appRef.attachView(ref.hostView);
      document.body.appendChild(ref.location.nativeElement);
      ref.changeDetectorRef.detectChanges();
    });
  }
}
